import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { ArrowDown, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useBookNow } from "@/hooks/useBookNow";

// Heavy 3D bundle (Three.js + drei + GLB + HDRI) deferred — page first-paints
// instantly with a lightweight placeholder, then upgrades to the canvas once
// the chunk is downloaded.
const HelmetCanvas = lazy(() => import("@/components/HelmetCanvas"));

function HelmetPlaceholder() {
  // Tiny CSS-only stand-in shown until the 3D chunk arrives. Same colors so
  // there's no visible "jump" when the canvas snaps in.
  return (
    <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
      <div className="w-[58%] aspect-square max-w-[420px] rounded-full bg-gradient-to-br from-[#1a0a0a] to-[#050505] border border-white/5 shadow-[0_0_60px_rgba(255,45,45,0.25)] animate-pulse" />
    </div>
  );
}

export const Hero = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const bookNow = useBookNow();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  // Screen-size detection
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Mount the 3D canvas immediately so the helmet is visible as soon as the
  // preloader lifts. The heavy paint offenders (SVG noise, huge blurs,
  // infinite motion divs) have been stripped on mobile, so parallel WebGL
  // init no longer freezes low-end phones.
  const [mountCanvas, setMountCanvas] = useState(false);
  useEffect(() => {
    // Kick module load; setMountCanvas immediately so the canvas starts
    // compiling in parallel with everything else.
    import("@/components/HelmetCanvas");
    setMountCanvas(true);
  }, []);

  const fadeUp = {
    hidden: { y: 50, opacity: 0 },
    show: (i = 0) => ({
      y: 0,
      opacity: 1,
      transition: { duration: 0.9, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Dark backdrop + ambient glow (single composition, no column divide) */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 diagonal-stripes opacity-25" />
        <div className="absolute top-[18%] left-1/2 -translate-x-1/2 w-[820px] h-[820px] rounded-full bg-[#FF2D2D]/18 blur-[160px]" />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-[#FFC700]/12 blur-[140px]" />
      </div>

      {/* Faint background watermark — vertical-centered */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 z-[1] flex items-center justify-center pointer-events-none overflow-hidden"
        aria-hidden="true"
      >
        <span className="font-display text-[32vw] md:text-[28vw] font-bold uppercase tracking-tighter text-white/[0.02] leading-none whitespace-nowrap">
          KARTING
        </span>
      </motion.div>

      {/* Centered single-column content */}
      <div className="relative z-[5] max-w-screen-2xl mx-auto px-6 md:px-12 min-h-screen flex flex-col items-center justify-center pt-28 md:pt-28 pb-24 md:pb-16">
        {/* Top overline */}
        <motion.div
          initial="hidden"
          animate="show"
          custom={0}
          variants={fadeUp}
          style={{ opacity }}
          className="flex items-center gap-3"
        >
          <span className="block w-2 h-2 bg-[#FF2D2D] animate-pulse" />
          <span className="font-mono-display text-[10px] md:text-[11px] tracking-[0.3em] text-[#FF2D2D] uppercase">
            {t.hero.overline}
          </span>
        </motion.div>

        {/* 3D helmet — bigger, stays in place, spins + reacts to scroll */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          style={{ y, opacity }}
          className="relative w-full h-[48vh] sm:h-[52vh] md:h-[58vh] lg:h-[62vh] mt-6 md:mt-2 mb-6 md:mb-2"
          data-testid="hero-helmet-wrap"
        >
          {mountCanvas && (
            <Suspense fallback={<HelmetPlaceholder />}>
              <HelmetCanvas scrollYProgress={scrollYProgress} isMobile={!isDesktop} />
            </Suspense>
          )}
          {!mountCanvas && <HelmetPlaceholder />}
        </motion.div>

        {/* Title — directly below the helmet */}
        <motion.div
          style={{ y, opacity }}
          className="relative w-full flex flex-col items-center text-center mt-6 md:mt-3"
        >
          <h1 className="font-display font-bold uppercase tracking-tighter leading-[0.85] text-white">
            <motion.span
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
            >
              <span>{t.hero.title1} </span>
              <span className="text-[#FF2D2D]">{t.hero.title2} </span>
              <span>{t.hero.title3}.</span>
            </motion.span>
          </h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-3 md:mt-4 text-sm md:text-base text-white/65 max-w-lg leading-relaxed font-light"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-4 md:mt-6 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              onClick={bookNow}
              className="group inline-flex items-center gap-3 bg-[#FF2D2D] text-white px-6 py-3 md:py-3.5 text-xs md:text-sm font-bold tracking-[0.16em] uppercase hover:bg-white hover:text-black transition-colors"
              data-testid="hero-book-btn"
            >
              {t.hero.cta_primary}
              <span className="inline-block w-6 h-px bg-current group-hover:w-10 transition-all" />
            </button>
            <button
              onClick={() => navigate("/track")}
              className="inline-flex items-center gap-3 border border-white/30 text-white px-6 py-3 md:py-3.5 text-xs md:text-sm font-medium tracking-[0.16em] uppercase hover:border-[#FF2D2D] hover:text-[#FF2D2D] transition-colors"
              data-testid="hero-watch-btn"
            >
              <MapPin size={14} />
              {t.hero.cta_secondary}
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom scroll indicator */}
      <motion.div
        style={{ opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[6] flex flex-col items-center gap-2 text-white/60"
        data-testid="scroll-indicator"
      >
        <span className="font-mono-display text-[10px] tracking-[0.3em] uppercase">
          {t.hero.scroll}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>


    </section>
  );
};

export default Hero;
